/**
 * V144 — Funnel + waitlist UI translations.
 *
 * Languages: en / sk / cs / de / pl / hu (6 core EU markets).
 * Other 20 langs fall back to EN at lookup time (existing
 * translations.js pattern).
 *
 * Strategy: localize ONLY the funnel UI chrome (CTAs, section
 * headers, badges, navigation copy). The agent PRODUCT copy
 * (taglines, pain quotes) stays in SK because that's Mika's
 * voice — translating it would dilute the emotional hook. For
 * non-SK visitors, we'll later add per-agent multi-lang fields
 * in the catalog (P2). Right now: a German visitor sees DE
 * navigation + SK product copy, which is still 100× better
 * than 100% SK lockout.
 */
export const funnelTranslations = {
  en: {
    // Hero
    funnel_eyebrow: "live agents · in pipeline · EU-native · GDPR",
    funnel_h1_a: "Rent an",
    funnel_h1_highlight: "AI agent",
    funnel_h1_b: ".",
    funnel_h1_c: "Reclaim your weekends.",
    funnel_subhead_a: "AI agents that do what you should be doing.",
    funnel_subhead_b: "For your e-shop. For your customers. For your peace of mind.",
    funnel_stat_live: "Live agents",
    funnel_stat_hours: "Hours saved weekly",
    funnel_stat_eur: "Saved monthly",
    funnel_stat_trial: "Trial",
    funnel_trial_days: "14 days",
    funnel_cta_trial: "Start 14-day trial",
    funnel_cta_view: "See all agents",
    // Pain section
    funnel_pain_eyebrow: "For the one who started this journey",
    funnel_pain_h2: "Remember why you started a business?",
    funnel_pain_p1_a: "For freedom. For growth. For doing something meaningful.",
    funnel_pain_p1_b: "Not to reply to your 18th review on Trustpilot at 11:47 PM on a Saturday.",
    funnel_pain_p2_a: "Your past self wouldn't believe how much time email, inventory, competitor checks take.",
    funnel_pain_p2_b: "Your future self will never forgive you for not delegating when you could.",
    funnel_pain_cta: "👉 Rent an agent. For the founder you were. For the one you'll become.",
    // Filters
    funnel_filter_h2: "Pick an agent for your problem.",
    cat_all: "All",
    cat_sales: "Sales",
    cat_marketing: "Marketing",
    cat_operations: "Operations",
    cat_compliance: "Compliance",
    cat_strategy: "Strategy",
    agent_card_detail: "Agent detail →",
    // Master table
    funnel_master_eyebrow: "Master comparison",
    funnel_master_h2: "Without an agent vs. with one — all in one place.",
    th_agent: "Agent",
    th_price: "Price",
    th_time: "h/week",
    th_eur: "€/mo",
    th_savings: "Savings",
    bundle_label: "Bundle 3-for-2 (most popular)",
    // Coming-soon
    coming_soon_eyebrow: "We're building & testing for you",
    coming_soon_h2_a: "+",
    coming_soon_h2_b: "more agents in pilot phase.",
    coming_soon_p_a: "These agents are in active pilot testing.",
    coming_soon_p_b: "Join the waitlist and get",
    coming_soon_offer_free: "30 days free",
    coming_soon_plus: "+",
    coming_soon_offer_off: "-25% lifetime price",
    coming_soon_p_c: "at launch.",
    coming_soon_priority_eyebrow: "Pilot priority signal",
    coming_soon_priority_p: "Agents with the most signups go into dev pipeline first. Your vote decides what we build next.",
    coming_soon_planned_price: "/mo planned price",
    coming_soon_roadmap: "Roadmap detail →",
    // Waitlist widget
    wl_inline_placeholder: "your@email.com",
    wl_inline_cta: "Notify me",
    wl_full_eyebrow: "🔬 We're building & testing · ETA",
    wl_full_h3_suffix: "is going to pilot phase.",
    wl_full_p_a: "Join the waitlist for",
    wl_full_offer_free: "30 days free",
    wl_full_offer_off: "-25% lifetime price",
    wl_full_p_b: "when we launch.",
    wl_full_note_placeholder: "Optional: what exactly do you need?",
    wl_full_cta: "Notify me when live",
    wl_email_invalid: "Enter a valid email",
    wl_error_generic: "Something went wrong",
    wl_success_eyebrow: "You're on the waitlist",
    wl_success_p_a: "We'll email you when we launch",
    wl_success_p_b: "to pilot. Earliest access:",
    wl_success_offer: "30 days free + -25% lifetime price.",
    // Demand counter
    dc_waiting_1: "waiting",
    dc_waiting_few: "waiting",
    dc_waiting_many: "waiting",
    // Closing CTA
    funnel_close_eyebrow: "Don't wait. Act today.",
    funnel_close_h2: "Your \"tomorrow\" may never come.",
    funnel_close_p1: "Not in a mystical sense, but a practical one. When you put off this last step till tomorrow, you train your brain to keep postponing everything else.",
    funnel_close_p2_a: "Today is the only day you can count on. Grab it by the horns.",
    funnel_close_p2_b: "Beat fear and inertia. Act now.",
    funnel_close_cta_emotional: "👉 Activate 14-day trial. No card. No commitment. No excuses.",
    funnel_close_cta_button: "Yes, I'm in",
    funnel_close_footer: "14 days free · cancel anytime · €79–€129/mo per agent · Bundle 3-for-2 = €198/mo",
    // Trust strip
    trust_eshops: "e-shops trust us",
    trust_free: "14 days free",
    trust_gdpr: "GDPR-compliant",
    trust_data: "EU data residency",
    trust_languages: "26 languages",
    // Pre-order (V144)
    preorder_eyebrow: "🔥 High demand — pre-order opens",
    preorder_label: "Pre-order €29 deposit",
    preorder_subtext: "Crediting toward your first month. Refundable if we don't ship by ETA.",
    preorder_success: "Pre-order received! Check your email.",
    // V145 — SixtySecondHero (FB-ad-ready scroll-stopper)
    sxs_h1_a: "Too easy",
    sxs_h1_b: "not to try.",
    sxs_subhead: "No coding. No setup. The agent does everything.",
    sxs_val1_eyebrow: "You save up to",
    sxs_val1_unit: "month",
    sxs_val2_eyebrow: "Fully automated",
    sxs_val2_title: "24/7 work",
    sxs_val3_eyebrow: "Battle-tested",
    sxs_val3_suffix: "agents",
    sxs_cta_button: "Start now",
    sxs_cta_subtext: "Activates in 2 minutes · 14 days free · no card",
    sxs_phone_subtitle: "EU-native · GDPR",
    sxs_phone_eyebrow: "AGENT OS",
    sxs_phone_welcome: "Welcome",
    sxs_phone_descline: "AI-powered automation. Fully managed. Start now.",
    sxs_phone_launch: "▸ Launch in 60 seconds",
    sxs_scan_cta: "Or get a free AI scan tailored to YOUR site →",
  },
  sk: {
    funnel_eyebrow: "live agentov · v príprave · EU-native · GDPR",
    funnel_h1_a: "Prenajmi si",
    funnel_h1_highlight: "AI agenta",
    funnel_h1_b: ".",
    funnel_h1_c: "Vráť si víkendy.",
    funnel_subhead_a: "AI agentov, ktorí robia to, čo by si mal robiť ty.",
    funnel_subhead_b: "Pre tvoj e-shop. Pre tvojich klientov. Pre tvoj pokoj.",
    funnel_stat_live: "Live agentov",
    funnel_stat_hours: "Ušetrí týždenne",
    funnel_stat_eur: "Ušetrí mesačne",
    funnel_stat_trial: "Trial",
    funnel_trial_days: "14 dní",
    funnel_cta_trial: "Spustí mi 14-dňový trial",
    funnel_cta_view: "Pozri si všetkých agentov",
    funnel_pain_eyebrow: "Pre toho, kto začal túto cestu",
    funnel_pain_h2: "Spomínaš si, prečo si začal podnikať?",
    funnel_pain_p1_a: "Pre slobodu. Pre rast. Pre pocit, že robíš niečo, čo má zmysel.",
    funnel_pain_p1_b: "Nie preto, aby si o 23:47 v sobotu odpovedal na 18. recenziu na Heureke.",
    funnel_pain_p2_a: "Tvoja minulá verzia by neuverila, koľko času ti zoberie e-mail, inventár, kontrola konkurencie.",
    funnel_pain_p2_b: "Tvoja budúca verzia ti nikdy neodpustí, že si to nedelegoval, keď si mohol.",
    funnel_pain_cta: "👉 Prenajmi si agenta. Pre toho, kto začal. Pre toho, kým sa staneš.",
    funnel_filter_h2: "Vyber agenta pre tvoj problém.",
    cat_all: "Všetci",
    cat_sales: "Predaj",
    cat_marketing: "Marketing",
    cat_operations: "Operatíva",
    cat_compliance: "Compliance",
    cat_strategy: "Stratégia",
    agent_card_detail: "Detail agenta →",
    funnel_master_eyebrow: "Master comparison",
    funnel_master_h2: "Bez agenta vs. s agentom — všetci na jednom mieste.",
    th_agent: "Agent",
    th_price: "Cena",
    th_time: "Čas/týž",
    th_eur: "€/mes",
    th_savings: "Úspora",
    bundle_label: "Bundle 3-za-2 (najpopulárnejšie)",
    coming_soon_eyebrow: "Pripravujeme & testujeme pre teba",
    coming_soon_h2_a: "+",
    coming_soon_h2_b: "ďalších agentov v pilot fáze.",
    coming_soon_p_a: "Tieto agenti práve testujeme s prvými pilot klientmi.",
    coming_soon_p_b: "Pridaj sa na waitlist a získaš",
    coming_soon_offer_free: "30 dní zadarmo",
    coming_soon_plus: "+",
    coming_soon_offer_off: "-25% lifetime price",
    coming_soon_p_c: "pri spustení.",
    coming_soon_priority_eyebrow: "Pilot priority signál",
    coming_soon_priority_p: "Agenti s najviac signupmi idú do dev pipeline ako prví. Tvoj hlas určuje, čo postavíme ďalej.",
    coming_soon_planned_price: "/mes plánovaná cena",
    coming_soon_roadmap: "Detail roadmap →",
    wl_inline_placeholder: "tvoj@email.sk",
    wl_inline_cta: "Daj mi vedieť",
    wl_full_eyebrow: "🔬 Pripravujeme & testujeme · ETA",
    wl_full_h3_suffix: "ide na pilot fázu.",
    wl_full_p_a: "Pridaj sa na waitlist a získaš",
    wl_full_offer_free: "30 dní zadarmo",
    wl_full_offer_off: "-25% lifetime price",
    wl_full_p_b: ", keď agenta spustíme.",
    wl_full_note_placeholder: "Voliteľné: čo presne potrebuješ?",
    wl_full_cta: "Daj mi vedieť keď bude live",
    wl_email_invalid: "Zadaj platný email",
    wl_error_generic: "Niečo zlyhalo",
    wl_success_eyebrow: "Si na waitliste",
    wl_success_p_a: "Pošleme ti email keď spustíme",
    wl_success_p_b: "do pilot fázy. Predtým ti dáme",
    wl_success_offer: "30 dní zadarmo + -25% lifetime price.",
    dc_waiting_1: "čaká",
    dc_waiting_few: "čakajú",
    dc_waiting_many: "čakajúcich",
    funnel_close_eyebrow: "Nečakaj. Konaj dnes.",
    funnel_close_h2: "Tvoje \"zajtra\" možno nikdy nepríde.",
    funnel_close_p1: "Nie v mystickom zmysle, ale v praktickom. Keď odkladáš tento posledný krok na zajtra, učíš svoj mozog, aby všetko neustále odkladal na neskôr.",
    funnel_close_p2_a: "Dnešok je jediný deň, na ktorý sa môžeš spoľahnúť. Chyť ho za pačesy.",
    funnel_close_p2_b: "Prekonaj strach a lenivosť a konaj teraz.",
    funnel_close_cta_emotional: "👉 Aktivuj 14-dňový trial. Bez karty. Bez záväzku. Bez výhovoriek.",
    funnel_close_cta_button: "Áno, idem do toho",
    funnel_close_footer: "14 dní zadarmo · zruš kedykoľvek · €79–€129/mes podľa agenta · Bundle 3-za-2 = €198/mes",
    trust_eshops: "e-shopov nám dôveruje",
    trust_free: "14 dní zadarmo",
    trust_gdpr: "GDPR-compliant",
    trust_data: "EU data residency",
    trust_languages: "26 jazykov",
    preorder_eyebrow: "🔥 Vysoký dopyt — predobjednávka spustená",
    preorder_label: "Predobjednaj €29 deposit",
    preorder_subtext: "Kredituje sa do prvého mesiaca. Refund ak nedoručíme do ETA.",
    preorder_success: "Predobjednávka prijatá! Skontroluj email.",
    sxs_h1_a: "Príliš jednoduché",
    sxs_h1_b: "na to, aby si to neskúsil.",
    sxs_subhead: "Žiadne nastavenia. Žiaden kód. Agent robí všetko za teba.",
    sxs_val1_eyebrow: "Ušetríš si až",
    sxs_val1_unit: "mes",
    sxs_val2_eyebrow: "Plne automatické",
    sxs_val2_title: "24/7 práca",
    sxs_val3_eyebrow: "Otestované v praxi",
    sxs_val3_suffix: "agentov",
    sxs_cta_button: "Spustiť teraz",
    sxs_cta_subtext: "Spustí sa za 2 minúty · 14 dní zadarmo · bez karty",
    sxs_phone_subtitle: "EU-native · GDPR",
    sxs_phone_eyebrow: "AGENT OS",
    sxs_phone_welcome: "Vitaj",
    sxs_phone_descline: "AI agent. Plne riadený. Spusti teraz.",
    sxs_phone_launch: "▸ Aktivácia za 60 sekúnd",
    sxs_scan_cta: "Alebo si dáj AI scan pre TVOJ web zadarmo →",
  },
  cs: {
    funnel_eyebrow: "live agentů · v přípravě · EU-native · GDPR",
    funnel_h1_a: "Pronajmi si",
    funnel_h1_highlight: "AI agenta",
    funnel_h1_b: ".",
    funnel_h1_c: "Vrať si víkendy.",
    funnel_subhead_a: "AI agentů, kteří dělají to, co bys měl dělat ty.",
    funnel_subhead_b: "Pro tvůj e-shop. Pro tvé klienty. Pro tvůj klid.",
    funnel_stat_live: "Live agentů",
    funnel_stat_hours: "Ušetří týdně",
    funnel_stat_eur: "Ušetří měsíčně",
    funnel_stat_trial: "Trial",
    funnel_trial_days: "14 dní",
    funnel_cta_trial: "Spustit 14denní trial",
    funnel_cta_view: "Prohlédnout všechny agenty",
    funnel_pain_eyebrow: "Pro toho, kdo začal tuto cestu",
    funnel_pain_h2: "Vzpomínáš si, proč jsi začal podnikat?",
    funnel_pain_p1_a: "Pro svobodu. Pro růst. Pro pocit, že děláš něco smysluplného.",
    funnel_pain_p1_b: "Ne proto, abys v 23:47 v sobotu odpovídal na 18. recenzi na Heuréce.",
    funnel_pain_p2_a: "Tvá minulá verze by neuvěřila, kolik času ti zabere e-mail, inventář, kontrola konkurence.",
    funnel_pain_p2_b: "Tvá budoucí verze ti nikdy neodpustí, žes to nedelegoval, když jsi mohl.",
    funnel_pain_cta: "👉 Pronajmi si agenta. Pro toho, kdo začal. Pro toho, kým se staneš.",
    funnel_filter_h2: "Vyber agenta pro svůj problém.",
    cat_all: "Všichni",
    cat_sales: "Prodej",
    cat_marketing: "Marketing",
    cat_operations: "Provoz",
    cat_compliance: "Compliance",
    cat_strategy: "Strategie",
    agent_card_detail: "Detail agenta →",
    funnel_master_eyebrow: "Master srovnání",
    funnel_master_h2: "Bez agenta vs. s ním — vše na jednom místě.",
    th_agent: "Agent",
    th_price: "Cena",
    th_time: "h/týden",
    th_eur: "€/měs",
    th_savings: "Úspora",
    bundle_label: "Bundle 3-za-2 (nejoblíbenější)",
    coming_soon_eyebrow: "Připravujeme & testujeme pro tebe",
    coming_soon_h2_a: "+",
    coming_soon_h2_b: "dalších agentů v pilot fázi.",
    coming_soon_p_a: "Tyto agenty právě testujeme s prvními pilot klienty.",
    coming_soon_p_b: "Přidej se na waitlist a získáš",
    coming_soon_offer_free: "30 dní zdarma",
    coming_soon_plus: "+",
    coming_soon_offer_off: "-25% lifetime cena",
    coming_soon_p_c: "při spuštění.",
    coming_soon_priority_eyebrow: "Pilot priority signál",
    coming_soon_priority_p: "Agenti s nejvíce signupy jdou do dev pipeline jako první. Tvůj hlas rozhoduje, co postavíme dál.",
    coming_soon_planned_price: "/měs plánovaná cena",
    coming_soon_roadmap: "Detail roadmap →",
    wl_inline_placeholder: "tvuj@email.cz",
    wl_inline_cta: "Dej mi vědět",
    wl_full_eyebrow: "🔬 Připravujeme & testujeme · ETA",
    wl_full_h3_suffix: "jde do pilot fáze.",
    wl_full_p_a: "Přidej se na waitlist a získáš",
    wl_full_offer_free: "30 dní zdarma",
    wl_full_offer_off: "-25% lifetime cena",
    wl_full_p_b: ", až agenta spustíme.",
    wl_full_note_placeholder: "Volitelné: co přesně potřebuješ?",
    wl_full_cta: "Dej mi vědět, až bude live",
    wl_email_invalid: "Zadej platný email",
    wl_error_generic: "Něco selhalo",
    wl_success_eyebrow: "Jsi na waitlistu",
    wl_success_p_a: "Pošleme ti email, jakmile spustíme",
    wl_success_p_b: "do pilot fáze. Předtím ti dáme",
    wl_success_offer: "30 dní zdarma + -25% lifetime cena.",
    dc_waiting_1: "čeká",
    dc_waiting_few: "čekají",
    dc_waiting_many: "čekajících",
    funnel_close_eyebrow: "Nečekej. Jednej dnes.",
    funnel_close_h2: "Tvoje \"zítra\" možná nikdy nepřijde.",
    funnel_close_p1: "Ne v mystickém smyslu, ale v praktickém. Když odkládáš tento poslední krok na zítra, učíš svůj mozek, aby všechno odkládal.",
    funnel_close_p2_a: "Dnešek je jediný den, na který se můžeš spolehnout. Chyť ho za pačesy.",
    funnel_close_p2_b: "Překonej strach a lenost a jednej teď.",
    funnel_close_cta_emotional: "👉 Aktivuj 14denní trial. Bez karty. Bez závazku. Bez výmluv.",
    funnel_close_cta_button: "Ano, jdu do toho",
    funnel_close_footer: "14 dní zdarma · zruš kdykoli · €79–€129/měs podle agenta · Bundle 3-za-2 = €198/měs",
    trust_eshops: "e-shopů nám důvěřuje",
    trust_free: "14 dní zdarma",
    trust_gdpr: "GDPR-compliant",
    trust_data: "EU data residency",
    trust_languages: "26 jazyků",
    preorder_eyebrow: "🔥 Vysoká poptávka — předobjednávka spuštěna",
    preorder_label: "Předobjednat €29 záloha",
    preorder_subtext: "Kredituje se do prvního měsíce. Refund pokud nedodáme do ETA.",
    preorder_success: "Předobjednávka přijata! Zkontroluj email.",
    sxs_h1_a: "Příliš jednoduché",
    sxs_h1_b: "na to, abys to nezkusil.",
    sxs_subhead: "Bez nastavení. Bez kódu. Agent dělá vše za tebe.",
    sxs_val1_eyebrow: "Ušetříš až",
    sxs_val1_unit: "měs",
    sxs_val2_eyebrow: "Plně automatické",
    sxs_val2_title: "24/7 práce",
    sxs_val3_eyebrow: "Otestované v praxi",
    sxs_val3_suffix: "agentů",
    sxs_cta_button: "Spustit teď",
    sxs_cta_subtext: "Spustí se za 2 minuty · 14 dní zdarma · bez karty",
    sxs_phone_subtitle: "EU-native · GDPR",
    sxs_phone_eyebrow: "AGENT OS",
    sxs_phone_welcome: "Vítej",
    sxs_phone_descline: "AI agent. Plně řízený. Spusť teď.",
    sxs_phone_launch: "▸ Aktivace za 60 sekund",
    sxs_scan_cta: "Nebo si vyžádej AI scan zdarma pro TVŮJ web →",
  },
  de: {
    funnel_eyebrow: "Live-Agenten · in Vorbereitung · EU-native · DSGVO",
    funnel_h1_a: "Miete dir einen",
    funnel_h1_highlight: "KI-Agenten",
    funnel_h1_b: ".",
    funnel_h1_c: "Hol dir die Wochenenden zurück.",
    funnel_subhead_a: "KI-Agenten, die das machen, was du tun solltest.",
    funnel_subhead_b: "Für deinen Shop. Für deine Kunden. Für deine Ruhe.",
    funnel_stat_live: "Live-Agenten",
    funnel_stat_hours: "Stunden/Woche gespart",
    funnel_stat_eur: "€ gespart/Monat",
    funnel_stat_trial: "Trial",
    funnel_trial_days: "14 Tage",
    funnel_cta_trial: "14-Tage-Trial starten",
    funnel_cta_view: "Alle Agenten ansehen",
    funnel_pain_eyebrow: "Für den, der diese Reise begonnen hat",
    funnel_pain_h2: "Erinnerst du dich, warum du gegründet hast?",
    funnel_pain_p1_a: "Für Freiheit. Für Wachstum. Für etwas Sinnvolles.",
    funnel_pain_p1_b: "Nicht um 23:47 Uhr am Samstag die 18. Rezension auf Trustpilot zu beantworten.",
    funnel_pain_p2_a: "Dein früheres Ich würde nicht glauben, wie viel Zeit E-Mail, Lager, Konkurrenzcheck fressen.",
    funnel_pain_p2_b: "Dein zukünftiges Ich wird dir nie verzeihen, dass du nicht delegiert hast, als du konntest.",
    funnel_pain_cta: "👉 Miete dir einen Agenten. Für den Gründer, der du warst. Für den, der du werden wirst.",
    funnel_filter_h2: "Wähle einen Agenten für dein Problem.",
    cat_all: "Alle",
    cat_sales: "Vertrieb",
    cat_marketing: "Marketing",
    cat_operations: "Operations",
    cat_compliance: "Compliance",
    cat_strategy: "Strategie",
    agent_card_detail: "Agent-Detail →",
    funnel_master_eyebrow: "Master-Vergleich",
    funnel_master_h2: "Ohne Agent vs. mit Agent — alles auf einen Blick.",
    th_agent: "Agent",
    th_price: "Preis",
    th_time: "h/Woche",
    th_eur: "€/Monat",
    th_savings: "Ersparnis",
    bundle_label: "Bundle 3-für-2 (beliebteste)",
    coming_soon_eyebrow: "Wir bauen & testen für dich",
    coming_soon_h2_a: "+",
    coming_soon_h2_b: "weitere Agenten in der Pilotphase.",
    coming_soon_p_a: "Diese Agenten testen wir gerade mit ersten Pilotkunden.",
    coming_soon_p_b: "Trag dich auf die Warteliste ein und bekomme",
    coming_soon_offer_free: "30 Tage kostenlos",
    coming_soon_plus: "+",
    coming_soon_offer_off: "-25% Lifetime-Preis",
    coming_soon_p_c: "beim Launch.",
    coming_soon_priority_eyebrow: "Pilot-Prioritäts-Signal",
    coming_soon_priority_p: "Agenten mit den meisten Signups gehen zuerst in die Dev-Pipeline. Deine Stimme entscheidet.",
    coming_soon_planned_price: "/Monat geplanter Preis",
    coming_soon_roadmap: "Roadmap-Detail →",
    wl_inline_placeholder: "deine@email.de",
    wl_inline_cta: "Benachrichtigen",
    wl_full_eyebrow: "🔬 Wir bauen & testen · ETA",
    wl_full_h3_suffix: "geht in die Pilotphase.",
    wl_full_p_a: "Trag dich auf die Warteliste ein für",
    wl_full_offer_free: "30 Tage kostenlos",
    wl_full_offer_off: "-25% Lifetime-Preis",
    wl_full_p_b: "beim Launch.",
    wl_full_note_placeholder: "Optional: Was genau brauchst du?",
    wl_full_cta: "Benachrichtige mich beim Launch",
    wl_email_invalid: "Gib eine gültige E-Mail ein",
    wl_error_generic: "Etwas ist schiefgelaufen",
    wl_success_eyebrow: "Du bist auf der Warteliste",
    wl_success_p_a: "Wir mailen dich an, wenn",
    wl_success_p_b: "in die Pilotphase geht. Vorab bekommst du",
    wl_success_offer: "30 Tage kostenlos + -25% Lifetime-Preis.",
    dc_waiting_1: "wartet",
    dc_waiting_few: "warten",
    dc_waiting_many: "warten",
    funnel_close_eyebrow: "Warte nicht. Handle heute.",
    funnel_close_h2: "Dein \"morgen\" kommt vielleicht nie.",
    funnel_close_p1: "Nicht mystisch — praktisch. Wenn du diesen letzten Schritt aufschiebst, trainierst du dein Gehirn, alles aufzuschieben.",
    funnel_close_p2_a: "Heute ist der einzige Tag, auf den du dich verlassen kannst.",
    funnel_close_p2_b: "Überwinde Angst und Trägheit. Handle jetzt.",
    funnel_close_cta_emotional: "👉 Aktiviere den 14-Tage-Trial. Keine Karte. Keine Verpflichtung. Keine Ausreden.",
    funnel_close_cta_button: "Ja, ich starte",
    funnel_close_footer: "14 Tage kostenlos · jederzeit kündbar · €79–€129/Monat je Agent · Bundle 3-für-2 = €198/Monat",
    trust_eshops: "Shops vertrauen uns",
    trust_free: "14 Tage gratis",
    trust_gdpr: "DSGVO-konform",
    trust_data: "EU-Datenresidenz",
    trust_languages: "26 Sprachen",
    preorder_eyebrow: "🔥 Hohe Nachfrage — Vorbestellung offen",
    preorder_label: "Vorbestellen €29 Anzahlung",
    preorder_subtext: "Wird auf den ersten Monat angerechnet. Rückerstattung bei Nicht-Lieferung bis ETA.",
    preorder_success: "Vorbestellung erhalten! Prüfe deine E-Mails.",
    sxs_h1_a: "Zu einfach,",
    sxs_h1_b: "um es nicht zu probieren.",
    sxs_subhead: "Kein Setup. Kein Code. Der Agent macht alles für dich.",
    sxs_val1_eyebrow: "Du sparst bis zu",
    sxs_val1_unit: "Monat",
    sxs_val2_eyebrow: "Vollautomatisch",
    sxs_val2_title: "24/7 Arbeit",
    sxs_val3_eyebrow: "Praxiserprobt",
    sxs_val3_suffix: "Agenten",
    sxs_cta_button: "Jetzt starten",
    sxs_cta_subtext: "Aktiv in 2 Minuten · 14 Tage gratis · keine Karte",
    sxs_phone_subtitle: "EU-native · DSGVO",
    sxs_phone_eyebrow: "AGENT OS",
    sxs_phone_welcome: "Willkommen",
    sxs_phone_descline: "KI-Agent. Vollständig verwaltet. Jetzt starten.",
    sxs_phone_launch: "▸ Aktivierung in 60 Sekunden",
    sxs_scan_cta: "Oder hol dir einen kostenlosen KI-Scan für DEINE Seite →",
  },
  pl: {
    funnel_eyebrow: "agentów live · w przygotowaniu · EU-native · RODO",
    funnel_h1_a: "Wynajmij",
    funnel_h1_highlight: "AI agenta",
    funnel_h1_b: ".",
    funnel_h1_c: "Odzyskaj weekendy.",
    funnel_subhead_a: "Agentów AI, którzy robią to, co powinieneś robić ty.",
    funnel_subhead_b: "Dla twojego sklepu. Dla twoich klientów. Dla twojego spokoju.",
    funnel_stat_live: "Agentów live",
    funnel_stat_hours: "h zaoszczędzonych/tydzień",
    funnel_stat_eur: "€ zaoszczędzonych/mies",
    funnel_stat_trial: "Trial",
    funnel_trial_days: "14 dni",
    funnel_cta_trial: "Uruchom 14-dniowy trial",
    funnel_cta_view: "Zobacz wszystkich agentów",
    funnel_pain_eyebrow: "Dla tego, kto rozpoczął tę podróż",
    funnel_pain_h2: "Pamiętasz, dlaczego zacząłeś biznes?",
    funnel_pain_p1_a: "Dla wolności. Dla wzrostu. Dla poczucia sensu.",
    funnel_pain_p1_b: "Nie po to, żeby o 23:47 w sobotę odpowiadać na 18. recenzję na Allegro.",
    funnel_pain_p2_a: "Twoja przeszła wersja by nie uwierzyła, ile czasu zabierają e-maile, magazyn, kontrola konkurencji.",
    funnel_pain_p2_b: "Twoja przyszła wersja nigdy ci nie wybaczy, że nie zdelegowałeś, kiedy mogłeś.",
    funnel_pain_cta: "👉 Wynajmij agenta. Dla tego, kim byłeś. Dla tego, kim się staniesz.",
    funnel_filter_h2: "Wybierz agenta do swojego problemu.",
    cat_all: "Wszyscy",
    cat_sales: "Sprzedaż",
    cat_marketing: "Marketing",
    cat_operations: "Operacje",
    cat_compliance: "Compliance",
    cat_strategy: "Strategia",
    agent_card_detail: "Szczegóły agenta →",
    funnel_master_eyebrow: "Master comparison",
    funnel_master_h2: "Bez agenta vs. z agentem — wszyscy w jednym miejscu.",
    th_agent: "Agent",
    th_price: "Cena",
    th_time: "h/tydz",
    th_eur: "€/mies",
    th_savings: "Oszczędność",
    bundle_label: "Bundle 3-za-2 (najpopularniejsze)",
    coming_soon_eyebrow: "Przygotowujemy & testujemy dla ciebie",
    coming_soon_h2_a: "+",
    coming_soon_h2_b: "kolejnych agentów w fazie pilotażu.",
    coming_soon_p_a: "Tych agentów testujemy z pierwszymi pilot klientami.",
    coming_soon_p_b: "Zapisz się na waitlist i dostaniesz",
    coming_soon_offer_free: "30 dni za darmo",
    coming_soon_plus: "+",
    coming_soon_offer_off: "-25% lifetime price",
    coming_soon_p_c: "przy starcie.",
    coming_soon_priority_eyebrow: "Sygnał priorytetu pilota",
    coming_soon_priority_p: "Agenci z najwięcej signupami idą do dev pipeline pierwsi. Twój głos decyduje.",
    coming_soon_planned_price: "/mies planowana cena",
    coming_soon_roadmap: "Szczegóły roadmapy →",
    wl_inline_placeholder: "twoj@email.pl",
    wl_inline_cta: "Powiadom mnie",
    wl_full_eyebrow: "🔬 Przygotowujemy & testujemy · ETA",
    wl_full_h3_suffix: "wchodzi w fazę pilota.",
    wl_full_p_a: "Zapisz się na waitlist dla",
    wl_full_offer_free: "30 dni za darmo",
    wl_full_offer_off: "-25% lifetime price",
    wl_full_p_b: "przy starcie.",
    wl_full_note_placeholder: "Opcjonalnie: czego dokładnie potrzebujesz?",
    wl_full_cta: "Powiadom mnie przy starcie",
    wl_email_invalid: "Podaj poprawny email",
    wl_error_generic: "Coś poszło nie tak",
    wl_success_eyebrow: "Jesteś na waitlist",
    wl_success_p_a: "Wyślemy email, gdy",
    wl_success_p_b: "wejdzie w pilota. Wcześniej dostaniesz",
    wl_success_offer: "30 dni za darmo + -25% lifetime price.",
    dc_waiting_1: "czeka",
    dc_waiting_few: "czekają",
    dc_waiting_many: "czekających",
    funnel_close_eyebrow: "Nie czekaj. Działaj dziś.",
    funnel_close_h2: "Twoje \"jutro\" może nigdy nie nadejść.",
    funnel_close_p1: "Nie mistycznie — praktycznie. Gdy odkładasz ten ostatni krok, uczysz mózg odkładać wszystko.",
    funnel_close_p2_a: "Dziś jest jedyny dzień, na który możesz liczyć.",
    funnel_close_p2_b: "Pokonaj strach i bierność. Działaj teraz.",
    funnel_close_cta_emotional: "👉 Aktywuj 14-dniowy trial. Bez karty. Bez zobowiązań. Bez wymówek.",
    funnel_close_cta_button: "Tak, biorę to",
    funnel_close_footer: "14 dni za darmo · anulujesz kiedy chcesz · €79–€129/mies za agenta · Bundle 3-za-2 = €198/mies",
    trust_eshops: "e-sklepów nam ufa",
    trust_free: "14 dni za darmo",
    trust_gdpr: "RODO-zgodne",
    trust_data: "EU data residency",
    trust_languages: "26 języków",
    preorder_eyebrow: "🔥 Wysokie zainteresowanie — przedsprzedaż otwarta",
    preorder_label: "Przedsprzedaż €29 zaliczka",
    preorder_subtext: "Zaliczona do pierwszego miesiąca. Refund jeśli nie dostarczymy do ETA.",
    preorder_success: "Przedsprzedaż przyjęta! Sprawdź email.",
    sxs_h1_a: "Zbyt proste,",
    sxs_h1_b: "żeby nie spróbować.",
    sxs_subhead: "Bez konfiguracji. Bez kodu. Agent robi wszystko za ciebie.",
    sxs_val1_eyebrow: "Zaoszczędzisz aż",
    sxs_val1_unit: "mies",
    sxs_val2_eyebrow: "W pełni automatyczny",
    sxs_val2_title: "Praca 24/7",
    sxs_val3_eyebrow: "Sprawdzonych",
    sxs_val3_suffix: "agentów",
    sxs_cta_button: "Uruchom teraz",
    sxs_cta_subtext: "Aktywacja w 2 minuty · 14 dni za darmo · bez karty",
    sxs_phone_subtitle: "EU-native · RODO",
    sxs_phone_eyebrow: "AGENT OS",
    sxs_phone_welcome: "Witaj",
    sxs_phone_descline: "AI agent. W pełni zarządzany. Uruchom teraz.",
    sxs_phone_launch: "▸ Aktywacja w 60 sekund",
    sxs_scan_cta: "Albo zamów darmowy AI scan dla TWOJEJ strony →",
  },
  hu: {
    funnel_eyebrow: "élő ügynök · előkészületben · EU-natív · GDPR",
    funnel_h1_a: "Bérelj",
    funnel_h1_highlight: "AI ügynököt",
    funnel_h1_b: ".",
    funnel_h1_c: "Vedd vissza a hétvégéidet.",
    funnel_subhead_a: "AI ügynökök, akik azt csinálják, amit neked kellene.",
    funnel_subhead_b: "A webáruházadért. Az ügyfeleidért. A nyugalmadért.",
    funnel_stat_live: "Élő ügynök",
    funnel_stat_hours: "Megtakarítás óra/hét",
    funnel_stat_eur: "Megtakarítás €/hó",
    funnel_stat_trial: "Trial",
    funnel_trial_days: "14 nap",
    funnel_cta_trial: "14 napos trial indítása",
    funnel_cta_view: "Összes ügynök",
    funnel_pain_eyebrow: "Annak, aki elkezdte ezt az utat",
    funnel_pain_h2: "Emlékszel, miért kezdtél vállalkozni?",
    funnel_pain_p1_a: "A szabadságért. A növekedésért. Hogy értelmes dolgot csinálj.",
    funnel_pain_p1_b: "Nem azért, hogy szombat este 23:47-kor a 18. értékelésre válaszolj.",
    funnel_pain_p2_a: "A múltbéli énedet sokkolná, mennyi időt vesz el az e-mail, raktár, versenytárs-figyelés.",
    funnel_pain_p2_b: "A jövőbeli éned soha nem fog megbocsátani, hogy nem delegáltad, amikor lehetett.",
    funnel_pain_cta: "👉 Bérelj egy ügynököt. Annak, aki voltál. Annak, akivé válsz.",
    funnel_filter_h2: "Válassz ügynököt a problémádhoz.",
    cat_all: "Mind",
    cat_sales: "Értékesítés",
    cat_marketing: "Marketing",
    cat_operations: "Műveletek",
    cat_compliance: "Megfelelőség",
    cat_strategy: "Stratégia",
    agent_card_detail: "Ügynök részletei →",
    funnel_master_eyebrow: "Master összehasonlítás",
    funnel_master_h2: "Ügynök nélkül vs. ügynökkel — mind egy helyen.",
    th_agent: "Ügynök",
    th_price: "Ár",
    th_time: "ó/hét",
    th_eur: "€/hó",
    th_savings: "Megtakarítás",
    bundle_label: "Bundle 3-2-ért (legnépszerűbb)",
    coming_soon_eyebrow: "Építjük & teszteljük neked",
    coming_soon_h2_a: "+",
    coming_soon_h2_b: "további ügynök pilot fázisban.",
    coming_soon_p_a: "Ezeket az ügynököket aktívan teszteljük pilot ügyfelekkel.",
    coming_soon_p_b: "Iratkozz fel a várólistára és kapsz",
    coming_soon_offer_free: "30 nap ingyen",
    coming_soon_plus: "+",
    coming_soon_offer_off: "-25% lifetime ár",
    coming_soon_p_c: "indításkor.",
    coming_soon_priority_eyebrow: "Pilot prioritás jelzés",
    coming_soon_priority_p: "A legtöbb feliratkozást gyűjtő ügynök kerül először dev pipeline-ba. A te szavazatod dönti el.",
    coming_soon_planned_price: "/hó tervezett ár",
    coming_soon_roadmap: "Roadmap részletei →",
    wl_inline_placeholder: "te@email.hu",
    wl_inline_cta: "Értesíts",
    wl_full_eyebrow: "🔬 Építjük & teszteljük · ETA",
    wl_full_h3_suffix: "pilot fázisba lép.",
    wl_full_p_a: "Iratkozz fel a várólistára:",
    wl_full_offer_free: "30 nap ingyen",
    wl_full_offer_off: "-25% lifetime ár",
    wl_full_p_b: "induláskor.",
    wl_full_note_placeholder: "Opcionális: mire van pontosan szükséged?",
    wl_full_cta: "Értesíts, amikor élő",
    wl_email_invalid: "Adj meg érvényes emailt",
    wl_error_generic: "Valami félresikerült",
    wl_success_eyebrow: "Fent vagy a várólistán",
    wl_success_p_a: "Emailt küldünk, amikor",
    wl_success_p_b: "pilot fázisba kerül. Előtte kapsz",
    wl_success_offer: "30 nap ingyen + -25% lifetime ár.",
    dc_waiting_1: "vár",
    dc_waiting_few: "várnak",
    dc_waiting_many: "várakoznak",
    funnel_close_eyebrow: "Ne várj. Cselekedj ma.",
    funnel_close_h2: "A \"holnap\" talán soha nem jön el.",
    funnel_close_p1: "Nem misztikusan — gyakorlatiasan. Ha halogatod ezt az utolsó lépést, az agyad mindent halogatni fog.",
    funnel_close_p2_a: "A ma az egyetlen nap, amire számíthatsz.",
    funnel_close_p2_b: "Győzd le a félelmet és a tehetetlenséget. Cselekedj most.",
    funnel_close_cta_emotional: "👉 Aktiváld a 14 napos trialt. Kártya nélkül. Kötelezettség nélkül. Kifogások nélkül.",
    funnel_close_cta_button: "Igen, megyek",
    funnel_close_footer: "14 nap ingyen · bármikor lemondható · €79–€129/hó/ügynök · Bundle 3-2-ért = €198/hó",
    trust_eshops: "webáruház bízik bennünk",
    trust_free: "14 nap ingyen",
    trust_gdpr: "GDPR-kompatibilis",
    trust_data: "EU adatlokáció",
    trust_languages: "26 nyelv",
    preorder_eyebrow: "🔥 Magas kereslet — előrendelés nyitva",
    preorder_label: "Előrendelés €29 letét",
    preorder_subtext: "Az első hónapba beszámítva. Visszatérítés, ha nem teljesítjük ETA-ra.",
    preorder_success: "Előrendelés rögzítve! Nézd meg az emailed.",
    sxs_h1_a: "Túl egyszerű ahhoz,",
    sxs_h1_b: "hogy ne próbáld ki.",
    sxs_subhead: "Nincs beállítás. Nincs kód. Az ügynök mindent megcsinál helyetted.",
    sxs_val1_eyebrow: "Megspórolsz akár",
    sxs_val1_unit: "hó",
    sxs_val2_eyebrow: "Teljesen automata",
    sxs_val2_title: "24/7 munka",
    sxs_val3_eyebrow: "Tesztelt",
    sxs_val3_suffix: "ügynök",
    sxs_cta_button: "Indítás most",
    sxs_cta_subtext: "Aktiválás 2 perc alatt · 14 nap ingyen · kártya nélkül",
    sxs_phone_subtitle: "EU-natív · GDPR",
    sxs_phone_eyebrow: "AGENT OS",
    sxs_phone_welcome: "Üdvözlünk",
    sxs_phone_descline: "AI ügynök. Teljes körű kezelés. Indítsd most.",
    sxs_phone_launch: "▸ Aktiválás 60 másodperc alatt",
    sxs_scan_cta: "Vagy kérj egy ingyenes AI scant a TE oldaladra →",
  },
};

/**
 * Lookup helper. Falls back to EN if requested lang is missing.
 * Usage: `const ft = funnelT(language); ft.funnel_cta_trial`
 */
export function funnelT(lang) {
  return funnelTranslations[lang] || funnelTranslations.en;
}
